// Invoice PDF rendering and data loading.
//
// Replaces three near-identical copies of generatePDF that had drifted apart.
// The document's CONTENT lives in invoiceModel.js, which stays importable
// without a DOM; this file owns the drawing and the Firebase lookup.

import { db, auth } from './firebase.js';
import { loadJsPDF } from '../vendor.js';
import { buildInvoiceModel } from './invoiceModel.js';

export { buildInvoiceModel };

/** Draw the model. The only part that needs jsPDF. */
export async function renderInvoicePdf(model) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF();
  const L = 20;              // left margin
  const R = 190;             // right edge
  let y;

  // Header band
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, 210, 34, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22).setFont(undefined, 'bold');
  doc.text('INVOICE', L, 17);
  doc.setFontSize(11).setFont(undefined, 'normal');
  doc.text(model.vendor.name, L, 26);

  if (model.invoiceNumber) {
    doc.setFontSize(10);
    doc.text(model.invoiceNumber, R, 17, { align: 'right' });
  }
  const contact = [model.vendor.email, model.vendor.website].filter(Boolean).join('  ');
  if (contact) doc.text(contact, R, 26, { align: 'right' });

  doc.setTextColor(15, 23, 42);

  // Paid stamp, instead of leaking internal status names like "SENT" to customers
  if (model.isPaid) {
    doc.setTextColor(22, 128, 61);
    doc.setFontSize(28).setFont(undefined, 'bold');
    doc.text('PAID', R, 52, { align: 'right' });
    if (model.paidLabel) {
      doc.setFontSize(9).setFont(undefined, 'normal');
      doc.text(model.paidLabel, R, 58, { align: 'right' });
    }
    doc.setTextColor(15, 23, 42);
  }

  // Bill to / dates
  y = 50;
  doc.setFontSize(9).setFont(undefined, 'bold');
  doc.text('BILL TO', L, y);
  doc.setFont(undefined, 'normal').setFontSize(10);
  y += 6;
  for (const line of [model.customer.name, model.customer.email, model.customer.phone].filter(Boolean)) {
    doc.text(line, L, y);
    y += 5.5;
  }

  if (!model.isPaid) {
    let dy = 50;
    const col = 130;
    doc.setFontSize(9).setFont(undefined, 'bold');
    doc.text('INVOICE DATE', col, dy);
    doc.text('DUE', col, dy + 12);
    doc.text('TERMS', col, dy + 24);
    doc.setFont(undefined, 'normal').setFontSize(10);
    doc.text(model.issuedLabel || '', col + 35, dy, { align: 'left' });
    doc.text(model.dueLabel || '', col + 35, dy + 12);
    doc.text(model.terms, col + 35, dy + 24);
  }

  // Service
  y = Math.max(y + 10, 90);
  doc.setDrawColor(226, 232, 240);
  doc.line(L, y, R, y);
  y += 9;
  doc.setFontSize(9).setFont(undefined, 'bold');
  doc.text('DESCRIPTION', L, y);
  doc.text('AMOUNT', R, y, { align: 'right' });
  y += 3;
  doc.line(L, y, R, y);

  y += 9;
  doc.setFontSize(11).setFont(undefined, 'normal');
  doc.text(model.service.type, L, y);
  doc.text(model.amountLabel, R, y, { align: 'right' });

  doc.setFontSize(9).setTextColor(100, 116, 139);
  for (const line of [
    model.service.location && `Service address: ${model.service.location}`,
    model.service.performedLabel && `Service date: ${model.service.performedLabel}`,
  ].filter(Boolean)) {
    y += 5;
    doc.text(line, L, y);
  }
  if (model.service.notes) {
    y += 5;
    for (const line of doc.splitTextToSize(model.service.notes, 150)) {
      doc.text(line, L, y);
      y += 4.5;
    }
  }
  doc.setTextColor(15, 23, 42);

  // Total
  y += 10;
  doc.line(L, y, R, y);
  y += 9;
  doc.setFontSize(12).setFont(undefined, 'bold');
  doc.text('Total', 150, y);
  doc.text(model.amountLabel, R, y, { align: 'right' });

  // Footer
  doc.setFontSize(9).setFont(undefined, 'normal').setTextColor(100, 116, 139);
  doc.text('Thank you for your business.', 105, 269, { align: 'center' });
  if (model.vendor.website) doc.text(model.vendor.website, 105, 275, { align: 'center' });
  if (model.vendor.reviewUrl) {
    doc.setTextColor(29, 78, 216);
    doc.textWithLink('Happy with the work? Leave us a review', 105, 282, {
      align: 'center',
      url: model.vendor.reviewUrl,
    });
    doc.setTextColor(100, 116, 139);
  }

  return doc;
}

const vendorCache = new Map();

/** Look up the vendor profile that brands the invoice. Cached per uid. */
export async function loadVendorProfile(uid = auth.currentUser && auth.currentUser.uid) {
  if (!uid) return {};
  if (vendorCache.has(uid)) return vendorCache.get(uid);
  try {
    const snap = await db.ref(`users/${uid}`).once('value');
    const profile = snap.val() || {};
    vendorCache.set(uid, profile);
    return profile;
  } catch (error) {
    console.error('Could not load vendor profile for the invoice:', error);
    return {};
  }
}

/** Build and draw in one call. */
export async function generateInvoicePdf({ job, invoice, uid }) {
  const vendor = await loadVendorProfile(uid);
  const model = buildInvoiceModel({ job, invoice, vendor });
  const doc = await renderInvoicePdf(model);
  return { doc, model };
}
